import React, { useEffect, useState, useRef } from 'react';
import { useFluidForm, useFormInstance } from "../FormContext";
import { FluidChat } from "../FluidChat.component";
import { EmbeddedStyles } from "../EmbeddedStyles";
import { getChatOptionsFromConfig, } from '../../../../shared';
import styled from "styled-components";
import { useRenderViewChange } from "../common";
import { SectionsProgress } from "./SectionsProgress";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { buildFormElements } from "./buildFormElements";
import { FormPanel } from "./FormPanel";
import { GripHorizontalIcon, MessageSquare, TextCursorInput } from 'lucide-react';
import { FormSubmittedMessage } from "../FormSubmittedMessage";
import { FluidValidation } from "../FluidValidation";
import { useFormInstanceSelect } from "../FormInstance.service";
// Card styles moved to FormPanel
export const UnifiedFluidForm = ({ showSubmitButton = true, onSubmit, validationResults, setStage, headless, ...props }) => {
    const form = useFluidForm();
    useFormInstanceSelect((state) => state.submissionId);
    const stage = useFormInstanceSelect((state) => state.submissionStage || "draft");
    if (!form) {
        return React.createElement("div", null, "Loading...");
    }
    const handleSubmit = (data, currentStage) => {
        console.log("Handling submit", data, currentStage);
        onSubmit?.(data);
    };
    return React.createElement("div", { style: { height: '100%' } },
        stage == "draft" && (React.createElement(UnifiedFormComponent, { ...props, showSubmitButton: showSubmitButton, onSubmit: (data) => handleSubmit(data, "draft"), headless: headless })),
        stage == "review" && (React.createElement(EmbeddedStyles, null,
            React.createElement(FluidValidation, { onSubmitForm: () => handleSubmit(form.getFormSubmission(), "review") }))),
        stage == "finalized" && (React.createElement(EmbeddedStyles, null,
            React.createElement(FormSubmittedMessage, { form: form }))));
};
export const UnifiedFormComponent = ({ onSubmit, showSubmitButton = true, onChange, disableUpdateHighlighting = false, includeChat = false, rounded = true, hideInputModes = false, headless = false, chatProps }) => {
    const form = useFluidForm();
    const [update, setUpdate] = useState(0);
    const instanceId = useFormInstance();
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const leftPanelRef = useRef(null);
    const topFormPanelRef = useRef(null);
    const bottomChatPanelRef = useRef(null);
    const inputModes = form.getAdditionalConfigValue("styleConfig", "inputModes") || [];
    const chatOptions = getChatOptionsFromConfig(form);
    // Get Fluid-specific configuration
    const collapseProgressPanel = form.getAdditionalConfigValue("styleConfig", "collapseProgressPanel") || false;
    const defaultMode = form.getAdditionalConfigValue("styleConfig", "defaultMode") || "form-chat";
    // Set initial collapsed states based on defaultMode
    const getInitialCollapseStates = () => {
        switch (defaultMode) {
            case "form":
                return { top: false, bottom: true };
            case "chat":
                return { top: true, bottom: false };
            case "form-chat":
            default:
                return { top: false, bottom: false };
        }
    };
    const initialStates = getInitialCollapseStates();
    const [isTopCollapsed, setIsTopCollapsed] = useState(initialStates.top);
    const isMobileInitial = window.innerWidth < 768;
    const [isLeftCollapsed, setIsLeftCollapsed] = useState(isMobileInitial || collapseProgressPanel);
    const [isBottomCollapsed, setIsBottomCollapsed] = useState(initialStates.bottom);
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [hGroupKey, setHGroupKey] = useState(0);
    const [isMobile, setIsMobile] = useState(isMobileInitial);
    const wasMobileRef = useRef(isMobileInitial);
    const desktopCollapseRef = useRef(collapseProgressPanel);
    useRenderViewChange("cards");
    let cards = buildFormElements(form);
    useEffect(() => {
        form.onUpdated((data) => {
            onChange?.(form.getFormSubmission());
        });
        form.events.on("onConfigChange", "DocFluidForm", (data) => {
            setUpdate(update + 1);
        });
    }, []);
    const handleMessageReceived = (message) => {
        form.mergeSubmissionData(message.data);
        if (message.messageType == "validation") {
            if (message.fieldValidations) {
                let fieldValidations = {};
                for (let [fieldPath, validationMessage] of Object.entries(message.fieldValidations)) {
                    fieldValidations[fieldPath] = {
                        fieldPath: fieldPath,
                        question: validationMessage,
                        type: "retry"
                    };
                }
                form.mergeValidationResults({
                    inferredData: message.data,
                    fieldValidations,
                    status: "error"
                });
            }
        }
        let firstField = message.messageFields?.[0];
        if (firstField) {
            form.setCurrentField(firstField);
        }
    };
    useEffect(() => {
        const measure = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth || 0);
            }
            const isMobileNow = window.innerWidth < 768;
            const wasMobile = wasMobileRef.current;
            if (isMobileNow && !wasMobile) {
                // Crossed into mobile: save current desktop state, force collapse
                setIsLeftCollapsed(prev => { desktopCollapseRef.current = prev; return true; });
                setHGroupKey(v => v + 1);
            }
            else if (!isMobileNow && wasMobile) {
                // Crossed back to desktop: restore previous state
                setIsLeftCollapsed(desktopCollapseRef.current);
                setHGroupKey(v => v + 1);
            }
            wasMobileRef.current = isMobileNow;
            setIsMobile(isMobileNow);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);
    // Apply initial collapse states based on configuration
    useEffect(() => {
        if (initialStates.top && topFormPanelRef.current) {
            topFormPanelRef.current.collapse();
        }
        if (initialStates.bottom && bottomChatPanelRef.current) {
            bottomChatPanelRef.current.collapse();
        }
    }, []);
    // Listen for configuration changes and update panel states in real-time
    useEffect(() => {
        const handleConfigChange = () => {
            const updatedCollapseProgressPanel = form.getAdditionalConfigValue("styleConfig", "collapseProgressPanel") || false;
            const updatedDefaultMode = form.getAdditionalConfigValue("styleConfig", "defaultMode") || "form-chat";
            // Update progress panel collapse state
            desktopCollapseRef.current = updatedCollapseProgressPanel;
            const isMobileNow = window.innerWidth < 768;
            setIsLeftCollapsed(isMobileNow || updatedCollapseProgressPanel);
            // Update form and chat panel states based on defaultMode
            switch (updatedDefaultMode) {
                case "form":
                    setIsTopCollapsed(false);
                    setIsBottomCollapsed(true);
                    if (topFormPanelRef.current)
                        topFormPanelRef.current.expand();
                    if (bottomChatPanelRef.current)
                        bottomChatPanelRef.current.collapse();
                    break;
                case "chat":
                    setIsTopCollapsed(true);
                    setIsBottomCollapsed(false);
                    if (topFormPanelRef.current)
                        topFormPanelRef.current.collapse();
                    if (bottomChatPanelRef.current)
                        bottomChatPanelRef.current.expand();
                    break;
                case "form-chat":
                default:
                    setIsTopCollapsed(false);
                    setIsBottomCollapsed(false);
                    if (topFormPanelRef.current)
                        topFormPanelRef.current.expand();
                    if (bottomChatPanelRef.current)
                        bottomChatPanelRef.current.expand();
                    break;
            }
        };
        // Listen to config changes
        form.events.on("onConfigChange", "UnifiedFluidForm-ConfigListener", handleConfigChange);
    }, [form]);
    const leftPanelClassName = "rounded-l-md " + (isMobile ? "max-w-12" : (isLeftCollapsed ? "max-w-12" : ""));
    const toggleBottomCollapse = () => {
        setIsBottomCollapsed(!isBottomCollapsed);
        if (isBottomCollapsed) {
            bottomChatPanelRef.current?.expand();
        }
        else {
            topFormPanelRef.current?.expand();
            bottomChatPanelRef.current?.collapse();
        }
    };
    const toggleTopCollapse = () => {
        setIsTopCollapsed(!isTopCollapsed);
        if (isTopCollapsed) {
            topFormPanelRef.current?.expand();
        }
        else {
            bottomChatPanelRef.current?.expand();
            topFormPanelRef.current?.collapse();
        }
    };
    // Single chat element that we portal either inside the Panel or outside it (body)
    const fluidChatElement = (React.createElement(FluidChat, { ...chatProps, formKey: form.getFormKey(), instanceId: instanceId, headless: false, title: "Form AI Assistant", onMessageReceived: handleMessageReceived, rounded: false, inputModes: inputModes, collapsible: true, autoStartForm: chatOptions.autoStart, collapsed: isBottomCollapsed }));
    return (React.createElement(EmbeddedStyles, { headless: headless, style: { height: '100%' } },
        React.createElement(Container, { ref: containerRef, headless: headless },
            React.createElement(PanelGroup, { key: `h-${hGroupKey}`, direction: "horizontal", className: "w-full flex-1" },
                React.createElement(Panel, { ref: leftPanelRef, className: leftPanelClassName },
                    React.createElement(SectionsProgress, { title: "Form Progress", activeIndex: currentCardIndex, collapsed: isMobile ? true : isLeftCollapsed, onToggleCollapse: (next) => { setIsLeftCollapsed(next); if (!isMobile)
                            setHGroupKey(v => v + 1); }, items: cards })),
                React.createElement(Panel, { defaultSize: 78, minSize: 50 },
                    React.createElement(PanelGroup, { direction: "vertical", style: { height: '100%', width: '100%' }, autoSaveId: "bottom_chat_save_id" },
                        React.createElement("div", { className: "flex w-full p-2 ps-3 f-border-b f-bg-gradient" },
                            React.createElement("span", { className: "flex-1 text-xl f-text" }, form.getFormLabel()),
                            React.createElement("div", { className: "flex flex-col items-center mr-2" },
                                React.createElement("button", { onClick: toggleTopCollapse, className: `p-1 ${isTopCollapsed ? `bg-gray-300` : `fluid-primary-bg`} text-white rounded-full transition-colors items-center`, title: "Show form view" },
                                    React.createElement(TextCursorInput, { className: "w-4 h-4 md:w-5 md:h-5" }))),
                            React.createElement("div", { className: "flex flex-col items-center" },
                                React.createElement("button", { onClick: toggleBottomCollapse, className: `p-1 ${isBottomCollapsed ? `bg-gray-300` : `fluid-primary-bg`} text-white rounded-full transition-colors items-center`, title: "Show chat view" },
                                    React.createElement(MessageSquare, { className: "w-4 h-4 md:w-5 md:h-5 p-[2px]" })))),
                        React.createElement(Panel, { ref: topFormPanelRef, defaultSize: 60, minSize: 30, collapsible: true, onCollapse: () => setIsTopCollapsed(true), onExpand: () => setIsTopCollapsed(false) },
                            React.createElement(FormPanel, { cards: cards, form: form, onSubmit: onSubmit })),
                        React.createElement(PanelResizeHandle, null,
                            React.createElement("div", { className: `h-2 f-bg-dark hover:bg-orange-100 cursor-row-resize flex items-center justify-center group transition-colors` },
                                React.createElement(GripHorizontalIcon, { className: "w-3 h-3 text-gray-600 group-hover:text-orange-500" }))),
                        React.createElement(Panel, { id: "bottom_chat", ref: bottomChatPanelRef, defaultSize: 40, minSize: 20, collapsible: true, onCollapse: () => setIsBottomCollapsed(true), onExpand: () => setIsBottomCollapsed(false) }, !isBottomCollapsed && fluidChatElement),
                        isBottomCollapsed && (React.createElement("div", { className: "flex flex-row w-full" }, fluidChatElement))))),
            isMobile && !isLeftCollapsed && (React.createElement("div", { className: "absolute inset-0 z-20 flex" },
                React.createElement(SectionsProgress, { className: "w-[70%] shadow-lg", title: "Form Progress", activeIndex: currentCardIndex, collapsed: false, onToggleCollapse: () => setIsLeftCollapsed(true), items: cards }),
                React.createElement("div", { className: "flex-1 bg-black/20", onClick: () => setIsLeftCollapsed(true) }))))));
};
// FormPanel extracted to its own file
const Container = styled.div.attrs({ className: "rounded-md f-bg f-border" }).withConfig({
    shouldForwardProp: (prop) => !['headless'].includes(prop),
}) `
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 60lvh;
  max-height: ${props => props.headless ? 'calc(100lvh - 1.5rem)' : '91lvh'};
  height: 100%;
  width: 100%;
`;
